import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Users, Sparkles } from 'lucide-react';
import { heroData, missionPoints, whatWeDoItems, getInvolvedOptions, faqItems } from '../data/mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';

const IconMap = {
  Heart: Heart,
  Users: Users,
  Sparkles: Sparkles,
  Shield: Shield
};

const HomePage = () => {
  return (
    <main>
      <SEO 
        title="Home"
        description="Scottish equine sanctuary providing safe refuge, rehabilitation, and rehoming for horses in need. Natural-living care for horses who can no longer be cared for by their owners."
        keywords="horse sanctuary Scotland, equine welfare, horse rescue Scotland, horse rehabilitation, natural horse care, Scottish horse charity"
        canonical="https://thegrazingtrust.org"
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container" style={{ position: 'relative', zIndex: 10 }}>
          <div className="hero-content">
            <span 
              className="body-small" 
              style={{ 
                background: 'var(--brand-primary)', 
                color: 'var(--brand-dark)',
                padding: '6px 16px',
                borderRadius: '20px',
                fontWeight: 600,
                display: 'inline-block',
                marginBottom: '24px'
              }}
            >
              Scottish Equine Sanctuary
            </span>
            <h1 className="display-large" style={{ marginBottom: '24px' }}>
              {heroData.headline}
            </h1>
            <p className="body-large" style={{ marginBottom: '32px', maxWidth: '500px' }}>
              {heroData.subheadline}
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Link to="/donate" className="btn-cta">
                <Heart size={20} style={{ marginRight: '8px' }} />
                {heroData.primaryCta}
              </Link>
              <img 
                src="/scvo-logo.png" 
                alt="SCVO Member" 
                style={{ 
                  height: '48px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
              <Link to="/help-a-horse" className="btn-secondary">
                {heroData.secondaryCta}
                <ArrowRight size={18} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
          </div>
        </div>
        <div className="hero-image-wrapper">
          <img 
            src={heroData.image} 
            alt="Horses grazing peacefully in a green pasture at The Grazing Trust sanctuary" 
            className="hero-image"
          />
          <div className="hero-overlay" />
        </div>
      </section>

      {/* Mission Points */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Mission</h2>
            <p className="body-medium">
              Providing compassionate care and secure futures for horses in need across Scotland.
            </p>
          </div>
          <div className="grid-4">
            {missionPoints.map((point, index) => (
              <div 
                key={point.id}
                className="network-card"
                style={{ 
                  textAlign: 'center',
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div 
                  style={{ 
                    width: '56px', 
                    height: '56px', 
                    background: 'var(--brand-primary)', 
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}
                >
                  <Shield size={28} color="var(--brand-dark)" />
                </div>
                <h3 className="heading-3" style={{ marginBottom: '8px' }}>{point.title}</h3>
                <p className="body-small">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">What We Do</h2>
            <p className="body-medium">
              From rescue to rehabilitation, and rehoming to lifelong sanctuary care.
            </p>
          </div>
          <div className="grid-2">
            {whatWeDoItems.map((item) => (
              <Link 
                key={item.id} 
                to={item.link}
                className="network-card hover-lift"
                style={{ textDecoration: 'none', overflow: 'hidden' }}
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="card-image"
                />
                <h3 className="heading-3" style={{ marginBottom: '8px' }}>{item.title}</h3>
                <p className="body-medium" style={{ marginBottom: '16px' }}>{item.description}</p>
                <span 
                  style={{ 
                    color: 'var(--brand-dark)', 
                    fontWeight: 600, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px' 
                  }}
                >
                  Learn more <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Get Involved</h2>
            <p className="body-medium">
              There are many ways you can help support horses in need.
            </p>
          </div>
          <div className="grid-3">
            {getInvolvedOptions.map((option) => {
              const Icon = IconMap[option.icon] || Heart;
              return (
                <div key={option.id} className="network-card" style={{ textAlign: 'center' }}>
                  <div 
                    style={{ 
                      width: '64px', 
                      height: '64px', 
                      background: 'var(--brand-primary)', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px'
                    }}
                  >
                    <Icon size={28} color="var(--brand-dark)" />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '12px' }}>{option.title}</h3>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>{option.description}</p>
                  <Link to={option.link} className="btn-primary">
                    {option.cta}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Frequently Asked Questions</h2>
            <p className="body-medium">
              Common questions about The Grazing Trust and how we can help.
            </p>
          </div>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} style={{ marginBottom: '12px' }}>
                  <AccordionTrigger 
                    style={{ 
                      background: 'white',
                      padding: '20px 24px',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-medium)'
                    }}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent 
                    style={{ 
                      padding: '16px 24px',
                      background: 'white',
                      borderRadius: '0 0 16px 16px',
                      marginTop: '-8px',
                      borderLeft: '1px solid var(--border-medium)',
                      borderRight: '1px solid var(--border-medium)',
                      borderBottom: '1px solid var(--border-medium)'
                    }}
                  >
                    <p className="body-medium">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Every Horse Deserves a Safe Future</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            Your support helps us provide refuge, care, and hope for horses who have nowhere else to turn.
          </p>
          <div className="cta-buttons">
            <Link to="/donate" className="btn-cta">
              <Heart size={20} style={{ marginRight: '8px' }} />
              Donate Now
            </Link>
            <Link to="/help-a-horse" className="btn-secondary" style={{ color: 'white', borderColor: 'white' }}>
              Get Help for a Horse
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
